import { useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { ChevronDown, ChevronUp, Eye, EyeOff, LogIn } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { useUiText } from "@/contexts/UiTextContext";
import MyPatriaWordmark from "@/components/patria/MyPatriaWordmark";
import api, { formatApiErrorDetail } from "@/lib/api";

const BRAND_IMAGE = "/brand/patria-church.jpeg";

export default function Login() {
  const { login, user } = useAuth();
  const { settings } = useSettings();
  const { t } = useUiText();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [helperOpen, setHelperOpen] = useState(false);
  // Demo credentials are served from GET /api/settings/demo-credentials so
  // a Super Admin can disable the helper for a production install without
  // shipping a frontend change. Rendered only when {enabled:true}.
  const [demoHelper, setDemoHelper] = useState({ enabled: false, note: null, accounts: [] });

  useEffect(() => {
    let cancelled = false;
    api.get("/settings/demo-credentials")
      .then(({ data }) => { if (!cancelled) setDemoHelper(data || { enabled: false, accounts: [] }); })
      .catch(() => { /* helper is best-effort — silence 404 in production installs */ });
    return () => { cancelled = true; };
  }, []);

  if (user) {
    const redirect = location.state?.from || "/";
    return <Navigate to={redirect} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome, ${u.name.split(" ")[0]}.`);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const fillDemo = (acct) => {
    setEmail(acct.email);
    // Password is intentionally not shipped over the wire — the reviewer
    // types it once (or the People Ops team shares it out-of-band).
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-sage-200">
      {/* Left: brand panel — deep forest green with the PATRIA CHURCH wordmark image. */}
      <div className="relative hidden lg:flex items-center justify-center overflow-hidden bg-forest-900">
        <img
          src={BRAND_IMAGE}
          alt="Patria Church"
          className="max-h-[70vh] w-auto object-contain"
          data-testid="login-brand-image"
        />
        <div className="absolute bottom-10 left-10 right-10 text-cream-100/85">
          <MyPatriaWordmark size="lg" className="!text-cream-100" data-testid="login-brand-mypatria" />
          <p className="mt-3 text-xs uppercase tracking-[0.4em]">People Operations</p>
          <blockquote className="mt-5 font-serif italic text-lg leading-relaxed max-w-md">
            &ldquo;Whatever you do, work heartily, as for the Lord.&rdquo;
          </blockquote>
          <p className="mt-1 text-[10px] font-sans uppercase tracking-widest text-terracotta-200">
            Colossians 3:23
          </p>
        </div>
      </div>

      {/* Right: form panel */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div
          className="patria-card w-full max-w-md p-8 sm:p-10"
          data-testid="login-card"
        >
          <div className="flex flex-col items-center gap-2 lg:hidden mb-6">
            <MyPatriaWordmark size="lg" />
            <p className="text-xs uppercase tracking-[0.4em] text-charcoal-muted">
              People Operations
            </p>
          </div>
          <h1
            data-testid="login-heading"
            className="text-3xl sm:text-4xl font-serif text-forest-900"
          >
            {t("login.welcome", "Welcome To myPatria")}
          </h1>
          <p className="mt-2 text-sm text-charcoal-soft" data-testid="login-subtitle">
            {settings?.welcome_message || t("login.subtitle", "Our People Operations Home.")}
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label htmlFor="email" className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1.5">
                Work email
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="patria-input w-full"
                placeholder="you@patriachurch.org"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPw ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="patria-input w-full pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-charcoal-muted hover:text-forest-600"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  data-testid="login-show-password"
                >
                  {showPw ? <EyeOff className="h-4 w-4" strokeWidth={1.5} /> : <Eye className="h-4 w-4" strokeWidth={1.5} />}
                </button>
              </div>
            </div>

            {error && (
              <p
                role="alert"
                data-testid="login-error"
                className="rounded-lg bg-terracotta-500/10 border border-terracotta-500/30 text-terracotta-700 px-3 py-2 text-sm"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="patria-btn-primary w-full"
              data-testid="login-submit-button"
            >
              <LogIn className="h-4 w-4" strokeWidth={1.5} />
              {submitting ? "Signing you in..." : "Sign in"}
            </button>
          </form>

          {/* Demo credentials helper — server-driven; hidden entirely when
              Super Admin disables it or when no demo accounts are seeded. */}
          {demoHelper.enabled && demoHelper.accounts.length > 0 && (
            <div className="mt-8 border-t border-sage-300/70 pt-4">
              <button
                type="button"
                onClick={() => setHelperOpen((v) => !v)}
                className="w-full flex items-center justify-between text-sm text-forest-700 hover:text-forest-900 font-medium"
                data-testid="demo-helper-toggle"
              >
                <span>Demo Credentials For Reviewers</span>
                {helperOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              {helperOpen && (
                <ul className="mt-3 space-y-2" data-testid="demo-helper-list">
                  {demoHelper.accounts.map((a) => (
                    <li
                      key={a.email}
                      className="flex items-center justify-between rounded-lg bg-sage-100 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="text-xs uppercase tracking-widest text-terracotta-500 font-medium">
                          {a.role}
                        </p>
                        <p className="text-sm text-charcoal truncate">{a.email}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => fillDemo(a)}
                        className="patria-btn-ghost text-xs"
                        data-testid={`demo-fill-${a.role.replace(/\s+/g, "-").toLowerCase()}`}
                      >
                        Use
                      </button>
                    </li>
                  ))}
                  {demoHelper.note && (
                    <li className="text-xs text-charcoal-muted italic mt-1">
                      {demoHelper.note}
                    </li>
                  )}
                </ul>
              )}
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-sage-300/70 text-center space-y-2">
            <p className="text-xs uppercase tracking-widest text-terracotta-500 font-medium">
              Need to speak up?
            </p>
            <p className="text-sm text-charcoal-soft">
              You don&apos;t need an account to raise a concern.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center pt-1">
              <Link
                to="/er/anonymous"
                className="patria-btn-secondary text-sm"
                data-testid="login-anon-intake-link"
              >
                Report Anonymously
              </Link>
              <Link
                to="/er/anonymous/portal"
                className="patria-btn-secondary text-sm"
                data-testid="login-anon-portal-link"
              >
                Check On An Anonymous Report
              </Link>
            </div>
            <div className="pt-3 text-xs text-charcoal-soft">
              New volunteer with an invite code?{" "}
              <Link
                to="/volunteer/register"
                className="text-forest-700 hover:text-forest-900 underline font-medium"
                data-testid="login-volunteer-register-link"
              >
                Register here.
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
